import { useEffect, useState } from "react";
import { api, apiError, imgUrl } from "../lib/api";
import { UploadZone } from "../components/UploadZone";
import { toast } from "sonner";
import { Plus, X, Sparkles } from "lucide-react";

const TYPES = ["all", "fabric", "print", "colorway"];
const SOURCES = [
  { key: "all", label: "All" },
  { key: "material", label: "Ad-Hoc" },
  { key: "plm", label: "From Libraries" },
];

export default function Library() {
  const [materials, setMaterials] = useState([]);
  const [filter, setFilter] = useState("all");
  const [source, setSource] = useState("all");
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ name: "", type: "fabric", image: null });
  const [saving, setSaving] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [genForm, setGenForm] = useState({ name: "", prompt: "" });
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const load = () => api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = materials.filter((m) => {
    if (filter !== "all" && m.type !== filter) return false;
    if (source !== "all" && (m.source || "material") !== source) return false;
    return true;
  });

  const upload = async () => {
    setSaving(true);
    try {
      await api.post("/materials", { name: form.name, type: form.type, image_b64: form.image });
      toast.success("Material added to library");
      setShowUpload(false);
      setForm({ name: "", type: "fabric", image: null });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const generatePrint = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/generate/print", genForm, { timeout: 180000 });
      setGenResult(data);
      toast.success("Print added to library");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Library</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Ad-Hoc Materials</h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-lg">Quick uploads and AI-generated prints for rendering. For governed master data (fabrics, prints, trims with specs), use <a href="/libraries" className="underline decoration-dotted hover:text-zinc-950">Libraries</a> — items there with an artwork appear here automatically with a PLM badge.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="library-generate-print-button" onClick={() => { setShowGen(true); setGenResult(null); }}
            className="inline-flex items-center gap-2 border border-zinc-300 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors">
            <Sparkles size={15} /> Generate print (AI)
          </button>
          <button data-testid="library-upload-button" onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
            <Plus size={15} /> Add material
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {TYPES.map((t) => (
            <button key={t} data-testid={`library-filter-${t}`} onClick={() => setFilter(t)}
              className={`px-4 py-2 rounded-md text-sm capitalize border transition-colors ${filter === t ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="h-6 w-px bg-zinc-200 hidden md:block" />
        <div className="flex gap-1 items-center">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mr-1">Source</span>
          {SOURCES.map((s) => (
            <button key={s.key} data-testid={`library-source-${s.key}`} onClick={() => setSource(s.key)}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${source === s.key ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filtered.map((m) => (
          <div key={m.id} data-testid={`library-material-${m.id}`} className="border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-sm hover:-translate-y-1 transition-transform duration-200 relative">
            {m.source === "plm" && (
              <span data-testid={`library-material-plm-${m.id}`} className="absolute top-2 left-2 bg-zinc-950 text-white text-[9px] uppercase tracking-[0.15em] font-medium px-1.5 py-0.5 rounded z-10">PLM</span>
            )}
            <img src={imgUrl(m.image_url)} alt={m.name} className="w-full h-36 object-cover" />
            <div className="p-3">
              <p className="text-sm font-medium truncate">{m.name}</p>
              <div className="flex items-center justify-between mt-0.5 gap-2">
                <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-400">{m.type}</p>
                {m.source === "plm" && m.plm_fabric_id && (
                  <a href="/libraries" className="text-[10px] text-zinc-500 hover:text-zinc-950 underline decoration-dotted">View spec</a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showGen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => !genLoading && setShowGen(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="library-generate-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Seamless all-over print</h2>
              <button data-testid="library-generate-close" onClick={() => setShowGen(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            {genResult ? (
              <div className="fade-up">
                <img data-testid="library-generated-print-image" src={imgUrl(genResult.image_url)} alt={genResult.name} className="w-full h-64 object-cover rounded-md border border-zinc-200" />
                <p className="text-sm font-medium mt-3">{genResult.name}</p>
                <p className="text-xs text-zinc-400">Saved to your library as a print — ready to apply to any garment.</p>
                <div className="mt-4 flex gap-2">
                  <button data-testid="library-generate-another" onClick={() => setGenResult(null)} className="flex-1 border border-zinc-300 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors">Generate another</button>
                  <button data-testid="library-generate-done" onClick={() => setShowGen(false)} className="flex-1 bg-zinc-950 text-white py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">Done</button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <input data-testid="library-print-name-input" value={genForm.name} onChange={(e) => setGenForm({ ...genForm, name: e.target.value })} placeholder="Print name — e.g. Tropical Botanics"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <textarea data-testid="library-print-prompt-input" value={genForm.prompt} onChange={(e) => setGenForm({ ...genForm, prompt: e.target.value })} rows={3}
                  placeholder="Describe the pattern — e.g. watercolor hibiscus flowers and palm leaves on cream, tropical resort feel"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
                <button data-testid="library-print-generate-button" onClick={generatePrint} disabled={!genForm.name || !genForm.prompt || genLoading}
                  className="w-full flex items-center justify-center gap-2 bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                  {genLoading ? (<><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating tile…</>) : (<><Sparkles size={15} /> Generate seamless print</>)}
                </button>
                {genLoading && <p className="text-xs text-zinc-400 text-center">Usually 15–30 seconds.</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowUpload(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="library-upload-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Add material</h2>
              <button data-testid="library-upload-close" onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <UploadZone value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="Upload swatch image" testId="library-material-upload-zone" />
              <input data-testid="library-material-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Material name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select data-testid="library-material-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="fabric">Fabric</option>
                <option value="print">Print</option>
                <option value="colorway">Colorway</option>
              </select>
              <button data-testid="library-material-save-button" onClick={upload} disabled={!form.name || !form.image || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Saving…" : "Add to library"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
